// Guided tour: dims the page, cuts a spotlight around a real element ([data-tour="…"]) and
// shows a bubble beside it. The user clicks the real thing; a step moves on with Next, by
// clicking the highlighted element (advanceOnClick), or once another element appears
// (advanceWhen: the next screen has loaded). A step can offer to type an example (fill).
// A step with `locked` still shows on a plan without the feature, as a read-only "Growth plan and up" step: its
// locked.target/body replace the usual ones and Next is the only way on.
// Started from anywhere via startTour(id); the active tour survives navigation (sessionStorage).
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { FiX } from "react-icons/fi";
import { TOURS } from "../lib/getStartedTours";
import { markGetStarted } from "../lib/getStartedService";

const KEY = "scoutm8.activeTour";
const EVENT = "scoutm8:tour";
const PAD = 6; // spotlight padding around the target
const GAP = 12; // bubble distance from the spotlight
const FIND_TIMEOUT_MS = 1500; // after this, a missing target shows the bubble centred instead
const LOAD_MAX_MS = 6000; // while the page still shows loading placeholders, wait up to this long
const pageLoading = () => Boolean(document.querySelector("[data-skeleton]"));
// A loading placeholder above or inside the target: filling it in can still move the target. Ones below can't.
const loadingAbove = (el) =>
  [...document.querySelectorAll("[data-skeleton]")].some(
    (n) => el.contains(n) || Boolean(n.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING),
  );
const SKELETON_WAIT_MS = 3000; // a held spotlight waits this long at most for loading placeholders to fill in
const HOLD_MAX_MS = 5000; // a page that never brings the next target: show the bubble again
const SETTLE_MS = 150; // after a page change, a target must sit still this long before the spotlight moves to it
let researchMarked = false; // once per page load is enough
let lastScrollAt = 0; // when anything on the page last scrolled
if (typeof window !== "undefined") window.addEventListener("scroll", () => (lastScrollAt = performance.now()), { capture: true, passive: true });

function readActive() {
  try {
    const v = JSON.parse(sessionStorage.getItem(KEY) || "null");
    return v && TOURS[v.id] ? v : null;
  } catch {
    return null;
  }
}

function writeActive(v) {
  try {
    if (v) sessionStorage.setItem(KEY, JSON.stringify(v));
    else sessionStorage.removeItem(KEY);
  } catch {
    /* private mode: the tour still runs for this page */
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: v }));
}

export function startTour(id) {
  if (TOURS[id]) writeActive({ id, step: 0 });
}

// The open walkthrough's id, or null.
export function activeTourId() {
  return readActive()?.id || null;
}

// The data-tour target of the step on screen, kept current; lets a page lay itself out for a step.
export function useTourTarget() {
  const read = () => {
    const a = readActive();
    return a ? TOURS[a.id]?.steps[a.step]?.target || null : null;
  };
  const [target, setTarget] = useState(read);
  useEffect(() => {
    const on = () => setTarget(read());
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  return target;
}

// A walkthrough is already open (a page's first-visit tour waits rather than replacing it).
export function isTourActive() {
  return Boolean(readActive());
}

const fade = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const pop = keyframes`from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; }`;

// A new spotlight opens out of the dim: its inside starts dimmed too, then clears.
const reveal = keyframes`
  from { background-color: var(--tour-dim); box-shadow: 0 0 0 9999px var(--tour-dim), 0 0 0 2px transparent; }
  to { background-color: transparent; box-shadow: 0 0 0 9999px var(--tour-dim), 0 0 0 2px var(--accent); }
`;

// Above every overlay in the app (brief sheet 9000, its confirm 9999, match overlay 10500), so steps inside them still show.
const Spot = styled.div`
  --tour-dim: color-mix(in srgb, #06101d 52%, transparent);
  position: fixed;
  z-index: 20000;
  border-radius: 10px;
  pointer-events: none;
  box-shadow: 0 0 0 9999px var(--tour-dim), 0 0 0 2px var(--accent);
  transition: top 0.18s ease, left 0.18s ease, width 0.18s ease, height 0.18s ease, background-color 0.25s ease,
    box-shadow 0.25s ease;
  animation: ${({ $fresh }) => ($fresh ? reveal : fade)} 0.3s ease-out;
  /* While the page changes the spotlight closes up: dimmed inside and out, no ring. */
  ${({ $held }) =>
    $held &&
    `background-color: var(--tour-dim); box-shadow: 0 0 0 9999px var(--tour-dim), 0 0 0 2px transparent;`}
`;

// Transparent panes around the spotlight: while a tour runs only the highlighted element and the bubble take clicks.
const Block = styled.div`
  position: fixed;
  z-index: 20000;
  background: transparent;
`;

const Bubble = styled.div`
  position: fixed;
  z-index: 20001;
  width: min(${({ $wide }) => ($wide ? 480 : 400)}px, calc(100vw - 32px));
  background: var(--surface);
  color: var(--ink);
  border: 1px solid var(--line);
  border-radius: 14px;
  box-shadow: var(--shadow-lg, 0 18px 40px -12px rgba(0, 0, 0, 0.35));
  padding: 20px 22px 16px;
  font-family: "Geist", ui-sans-serif, system-ui, sans-serif;
  font-size: 14.5px;
  line-height: 1.5;
  animation: ${pop} 0.25s ease-out;
  transition: top 0.2s ease, left 0.2s ease, opacity 0.15s ease;

  /* Long steps plus 44px touch-target buttons can outgrow a phone screen: scroll inside instead. */
  @media (max-width: 450px) {
    max-height: calc(100dvh - 32px);
    overflow-y: auto;
  }
`;

const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 6px;
`;

const Title = styled.div`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 27px;
  line-height: 1.12;
  margin: 0 26px 8px 0;
`;

const Body = styled.div`
  color: var(--ink-soft, var(--ink-muted));
`;

const Foot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
`;

const Dots = styled.div`
  display: flex;
  gap: 5px;
  flex: 1 1 auto;
  min-width: 40px;
  span {
    width: 6px;
    height: 6px;
    border-radius: 99px;
    background: var(--line-strong, var(--line));
  }
  span.on {
    background: var(--accent);
  }
`;

const Btn = styled.button`
  height: 30px;
  padding: 0 12px;
  flex-shrink: 0;
  white-space: nowrap;
  border-radius: 8px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid
    ${({ $primary, $soft }) =>
      $primary ? "var(--accent)" : $soft ? "color-mix(in srgb, var(--accent) 45%, transparent)" : "var(--line)"};
  background: ${({ $primary, $soft }) => ($primary ? "var(--accent)" : $soft ? "var(--accent-soft)" : "transparent")};
  color: ${({ $primary, $soft }) => ($primary ? "var(--on-fill)" : $soft ? "var(--accent)" : "var(--ink)")};
  &:hover:not(:disabled) {
    filter: brightness(0.96);
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

const LockPill = styled.span`
  margin-left: 8px;
  padding: 1px 7px;
  border-radius: 6px;
  font-size: 10.5px;
  letter-spacing: 0.02em;
  text-transform: none;
  color: var(--secondary);
  background: var(--secondary-soft);
`;

const Hint = styled.span`
  flex: 1 1 160px;
  font-size: 12px;
  color: var(--ink-muted);
  text-align: right;
`;

const spin = keyframes`to { transform: rotate(360deg); }`;

const Waiting = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: var(--ink-muted);
  &::before {
    content: "";
    width: 12px;
    height: 12px;
    border-radius: 99px;
    border: 2px solid var(--line);
    border-top-color: var(--accent);
    animation: ${spin} 0.8s linear infinite;
  }
`;

const Close = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--ink-muted);
  cursor: pointer;
  display: grid;
  place-items: center;
  &:hover {
    background: var(--bg-2, var(--line));
    color: var(--ink);
  }
`;

// The input/textarea a step points at (the element itself, or the first one inside it).
// advanceWhen / skipWhen take one data-tour name or a list; true when any of them is on screen.
function onScreen(names) {
  return [].concat(names || []).some((n) => document.querySelector(`[data-tour="${n}"]`));
}

// On screen, not clipped by a scrolling panel it sits in, and not hidden under something else (a sticky header); the tour's own layers don't count.
function inView(el, r) {
  if (!(r.top >= 0 && r.bottom <= window.innerHeight && r.height > 0)) return false;
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    if (!/(auto|scroll|hidden)/.test(getComputedStyle(p).overflowY)) continue;
    const c = p.getBoundingClientRect();
    if (r.top < c.top - 1 || r.bottom > c.bottom + 1) return false;
  }
  const x = r.left + r.width / 2;
  const y = r.top + Math.min(r.height / 2, 12);
  const hit = (document.elementsFromPoint?.(x, y) || []).find((n) => !n.closest("[data-tour-layer]"));
  return !hit || el.contains(hit);
}

function fieldIn(target) {
  const host = document.querySelector(`[data-tour="${target}"]`);
  return host?.matches("input, textarea") ? host : host?.querySelector("textarea, input");
}

// Type into a React-controlled input/textarea the way a user would, so the page's state updates.
function fillField(target, value) {
  const el = fieldIn(target);
  if (!el) return false;
  const proto = el.tagName === "TEXTAREA" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, "value").set.call(el, value);
  el.dispatchEvent(new Event("input", { bubbles: true }));
  el.focus();
  return true;
}

// Where the bubble goes: below the target if it fits, else above, else centred.
function placeBubble(rect, size) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = size?.w || 400;
  const h = size?.h || 200;
  if (!rect) return { top: Math.max(16, (vh - h) / 2), left: Math.max(16, (vw - w) / 2) };
  const left = Math.min(Math.max(16, rect.left + rect.width / 2 - w / 2), vw - w - 16);
  const below = rect.bottom + PAD + GAP;
  if (below + h <= vh - 16) return { top: below, left };
  const above = rect.top - PAD - GAP - h;
  if (above >= 16) return { top: above, left };
  // Tall target: sit beside it, or over its lower part as a last resort.
  if (rect.right + GAP + w <= vw - 16) return { top: Math.min(Math.max(16, rect.top), vh - h - 16), left: rect.right + GAP };
  // Never above the viewport: on a short phone screen a tall bubble would lose its title and Close.
  return { top: Math.max(16, vh - h - 16), left };
}

// Click each [data-tour] element in turn, waiting for it to render and the page to update; a missing one is skipped.
async function clickInOrder(targets) {
  const frame = () => new Promise((r) => requestAnimationFrame(() => r()));
  for (const t of targets) {
    const started = performance.now();
    let el = document.querySelector(`[data-tour="${t}"]`);
    while (!el && performance.now() - started < FIND_TIMEOUT_MS) {
      await frame();
      el = document.querySelector(`[data-tour="${t}"]`);
    }
    el?.click();
    await frame();
    await frame();
  }
}

// The four panes around the spotlight's hole, or one pane over everything while there's no hole.
function blockPanes(rect) {
  if (!rect) return [{ top: 0, left: 0, right: 0, bottom: 0 }];
  const t = Math.max(0, rect.top - PAD);
  const b = rect.bottom + PAD;
  const l = Math.max(0, rect.left - PAD);
  const r = rect.right + PAD;
  return [
    { top: 0, left: 0, right: 0, height: t },
    { top: b, left: 0, right: 0, bottom: 0 },
    { top: t, left: 0, width: l, height: b - t },
    { top: t, left: r, right: 0, height: b - t },
  ];
}

// Can a wheel or swipe of (dx, dy) scroll something between node and stop (inclusive)?
function canScrollWithin(node, stop, dx, dy) {
  for (let el = node; el && el.nodeType === 1; el = el.parentElement) {
    const cs = getComputedStyle(el);
    if (dy && /(auto|scroll)/.test(cs.overflowY) && el.scrollHeight > el.clientHeight) {
      if (dy > 0 ? el.scrollTop + el.clientHeight < el.scrollHeight - 1 : el.scrollTop > 0) return true;
    }
    if (dx && /(auto|scroll)/.test(cs.overflowX) && el.scrollWidth > el.clientWidth) {
      if (dx > 0 ? el.scrollLeft + el.clientWidth < el.scrollWidth - 1 : el.scrollLeft > 0) return true;
    }
    if (el === stop) break;
  }
  return false;
}

const SCROLL_KEYS = new Set(["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "PageUp", "PageDown", "Home", "End"]);

// A walkthrough bug must never take the page down: end the tour and carry on.
class TourBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(err) {
    console.error("Guided tour stopped:", err);
    writeActive(null);
    // With the tour cleared, remount so the next "Show me" works.
    setTimeout(() => this.setState({ failed: false }), 0);
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

export default function GuidedTour() {
  return (
    <TourBoundary>
      <GuidedTourInner />
    </TourBoundary>
  );
}

function GuidedTourInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(readActive);
  const [rect, setRect] = useState(null);
  const rectRef = useRef(null); // the spotlight's current target box, read when a step starts
  rectRef.current = rect;
  const [missing, setMissing] = useState(false);
  const [size, setSize] = useState(null); // the bubble's own size, from a ResizeObserver
  // Plan locks seen so far this tour, so a locked step stays read-only once its lock has shown.
  const [seen, setSeen] = useState({});
  const [filled, setFilled] = useState(true); // for requireFilled steps: has the field got a value yet
  // The step's target left the screen because the user clicked it (a page is changing): hide the
  // bubble and keep the spotlight still until the next target has settled.
  const [hold, setHold] = useState(false);
  // Back on an action step whose result is still on screen (e.g. the form is already open): Next is offered.
  const [already, setAlready] = useState(false);
  const holdRef = useRef(false);
  const settleRef = useRef({ key: "", since: 0 }); // the held target's last box and since when; survives effect re-runs
  const [entry, setEntry] = useState(0); // bumps when a hold ends: spotlight and bubble appear fresh, no slide
  const release = useCallback(() => {
    if (!holdRef.current) return;
    holdRef.current = false;
    setHold(false);
    setEntry((n) => n + 1);
  }, []);
  const bubbleRef = useRef(null);

  useEffect(() => {
    const on = (e) => {
      if (!e.detail || e.detail.step === 0) setSeen({});
      if (!e.detail) {
        holdRef.current = false;
        setHold(false);
      }
      setActive(e.detail || null);
    };
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);

  // Opening a company's research page is the research step done; the page keeps no history of its own.
  useEffect(() => {
    if (!researchMarked && /^\/research\/.+/.test(location.pathname)) {
      researchMarked = true;
      markGetStarted({ step: "research" });
    }
  }, [location.pathname]);

  const tour = active ? TOURS[active.id] : null;
  const rawStep = tour ? tour.steps[active.step] : null;
  const isLocked = Boolean(rawStep?.locked && seen[rawStep.locked.when]);
  const step = useMemo(
    () =>
      rawStep && isLocked
        ? {
            ...rawStep,
            ...rawStep.locked,
            locked: undefined,
            advanceWhen: undefined,
            advanceOnClick: false,
            fill: undefined,
            requireFilled: false,
            undo: rawStep.locked.undo,
            isLocked: true,
          }
        : rawStep,
    [rawStep, isLocked],
  );
  const onRoute = Boolean(tour && location.pathname.startsWith(tour.route.split("?")[0]));

  // Take the user to the tour's page first; leaving it afterwards ends the tour.
  useEffect(() => {
    if (!tour) return;
    if (onRoute && !active.arrived) writeActive({ ...active, arrived: true });
    else if (!onRoute && !active.arrived) navigate(tour.route);
    else if (!onRoute) writeActive(null);
  }, [tour, onRoute, active, navigate]);

  const go = useCallback(
    (delta, skipped = false) => {
      if (!active || !tour) return;
      // A step after a form was submitted goes Back to the start of that form (backStep): the
      // steps in between point at a form that's gone.
      const from = tour.steps[active.step];
      const next = delta < 0 && from?.backStep != null && !skipped ? from.backStep : active.step + delta;
      if (next >= tour.steps.length) {
        writeActive(null);
        if (tour.marks) markGetStarted({ step: tour.marks });
        return;
      }
      const to = { ...active, step: Math.max(0, next), dir: delta < 0 ? -1 : 1 };
      // A step the screen changes between pops in fresh once the new screen settles, instead of sliding across.
      const screenChange =
        delta > 0
          ? Boolean(step?.advanceWhen || step?.advanceOnClick)
          : Boolean(from?.backStep != null || step?.backTo || step?.undo?.length);
      if (screenChange) {
        holdRef.current = true;
        setHold(true);
      }
      // Back puts the previous step's screen back: its page (backTo) and whatever this step's action opened (undo).
      // A step passed by via skipWhen never opened its screen, so it undoes nothing unless it says so (undoOnSkip).
      // A step that needs a screen closed first (`before`) clicks those elements on the way forward.
      const ahead = delta > 0 ? tour.steps[to.step]?.before : null;
      if (ahead?.length) {
        holdRef.current = true;
        setHold(true);
        clickInOrder(ahead).then(() => {
          const now = readActive();
          if (now && now.id === active.id && now.step === active.step) writeActive(to);
        });
        return;
      }
      const undoing = delta < 0 && (!skipped || step?.undoOnSkip);
      if (undoing && step?.backTo) navigate(step.backTo);
      if (undoing && step?.undo?.length) {
        clickInOrder(step.undo).then(() => {
          const now = readActive();
          if (now && now.id === active.id && now.step === active.step) writeActive(to);
        });
        return;
      }
      writeActive(to);
    },
    [active, tour, step, navigate],
  );

  // Track the target every frame while open: pages render late, scroll and resize. The first
  // pass runs before paint but only measures, so a target already on screen gets the bubble in
  // place at once; skipping and advancing happen in later frames, and at most once per step.
  useLayoutEffect(() => {
    if (!step || !onRoute) return undefined;
    let raf = 0;
    let scrolled = false;
    let moved = false;
    const started = performance.now();
    const measure = (el) => {
      const r = el.getBoundingClientRect();
      setRect((prev) =>
        prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height
          ? prev
          : { top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom },
      );
      return r;
    };
    const first = step.target ? document.querySelector(`[data-tour="${step.target}"]`) : null;
    // Target not on screen yet (a page still loading), or no spotlight to slide from (a tour just
    // opened): dim only until the target shows up and settles, then open the spotlight right on it.
    // A target off screen too: dim, jump the page to it in one go, then open the spotlight there (no slow scroll with the light chasing it).
    if (step.target && (!first || !rectRef.current || !inView(first, first.getBoundingClientRect())) && !holdRef.current) {
      holdRef.current = true;
      setHold(true);
    }
    // Coming from a page change, wait for the new target to stop moving; otherwise place it at once.
    let settled = !holdRef.current;
    let found = Boolean(first);
    let goneAt = 0;
    let wasAbsent = false;
    let wasMissing = false; // the bubble went up centred; when the target turns up it reappears there
    if (settled) setAlready(Boolean(step.advanceWhen && onScreen(step.advanceWhen)));
    if (first && settled) measure(first);
    else if (!first && settled) setRect((prev) => (prev ? null : prev));
    setMissing((prev) => (prev ? false : prev));
    const keys = [...new Set(tour.steps.map((x) => x.locked?.when).filter(Boolean))];
    const tick = () => {
      if (moved) return;
      if (step.skipWhen && onScreen(step.skipWhen)) {
        moved = true;
        go(active.dir === -1 ? -1 : 1, true);
        return;
      }
      for (const k of keys) {
        if (document.querySelector(`[data-tour="${k}"]`)) setSeen((prev) => (prev[k] ? prev : { ...prev, [k]: true }));
      }
      // Coming back with Back, the screen is often already past this step: stay, don't bounce forward,
      // until the user undoes it (e.g. back on the list); from then on the step needs the action again.
      if (step.advanceWhen) {
        const done = onScreen(step.advanceWhen);
        if (!done) wasAbsent = true;
        if (done && (active.dir !== -1 || wasAbsent)) {
          moved = true;
          go(1);
          return;
        }
        if (settled) setAlready((prev) => (prev === done ? prev : done));
      }
      const el = step.target ? document.querySelector(`[data-tour="${step.target}"]`) : null;
      if (step.requireFilled) {
        const has = Boolean(fieldIn(step.target)?.value?.trim());
        setFilled((prev) => (prev === has ? prev : has));
      }
      if (el && !settled) {
        // Still dimmed: bring the target into view in one jump, and again if rows still loading above push it away,
        // so the spotlight only opens once the page has stopped moving.
        const v = el.getBoundingClientRect();
        if ((!scrolled || v.height < window.innerHeight) && !inView(el, v)) {
          el.scrollIntoView({ block: "center", behavior: "instant" });
        }
        scrolled = true;
        const b = el.getBoundingClientRect();
        const key = `${b.top}|${b.left}|${b.width}|${b.height}`;
        const now = performance.now();
        const st = settleRef.current;
        if (key !== st.key) {
          settleRef.current = { key, since: now };
        } else if (now - st.since >= SETTLE_MS && (!loadingAbove(el) || now - started > SKELETON_WAIT_MS)) {
          settled = true;
          release();
        }
        if (!settled) {
          raf = requestAnimationFrame(tick);
          return;
        }
      }
      if (el) {
        found = true;
        goneAt = 0;
        if (holdRef.current) {
          release();
        }
        if (wasMissing) {
          wasMissing = false;
          setEntry((n) => n + 1);
        }
        const r = measure(el);
        // Off screen (it turned up late, or rows loading above pushed it away): dim again and jump to it in one go.
        // A highlight the user is scrolling through is left alone.
        const off = r.bottom < 0 || r.top > window.innerHeight;
        if (off && (!scrolled || (r.height < window.innerHeight && performance.now() - lastScrollAt > 250))) {
          holdRef.current = true;
          setHold(true);
          settled = false;
          raf = requestAnimationFrame(tick);
          return;
        }
        scrolled = true;
        setMissing((prev) => (prev ? false : prev));
      } else if (found && (step.advanceWhen || step.advanceOnClick) && performance.now() - (goneAt || (goneAt = performance.now())) < HOLD_MAX_MS) {
        holdRef.current = true;
        setHold(true);
      } else if (goneAt) {
        release();
        setRect((prev) => (prev ? null : prev));
        setMissing((prev) => (prev ? prev : true));
        wasMissing = true;
      } else if (!settled) {
        // Keep the dim while the page is still loading, so the bubble doesn't show up centred first.
        const waited = performance.now() - started;
        if ((waited > FIND_TIMEOUT_MS && !pageLoading()) || waited > LOAD_MAX_MS) {
          release();
          settled = true;
        }
      } else if (!step.target || performance.now() - started > FIND_TIMEOUT_MS) {
        setRect((prev) => (prev ? null : prev));
        setMissing((prev) => (prev ? prev : true));
        wasMissing = true;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      moved = true;
      cancelAnimationFrame(raf);
    };
  }, [step, onRoute, go, active, tour, release]);

  // Clicking the highlighted element itself moves the tour on, when the step asks for it.
  useEffect(() => {
    if (!step?.advanceOnClick || !step.target) return undefined;
    const onClick = (e) => {
      if (e.target.closest?.(`[data-tour="${step.target}"]`)) setTimeout(() => go(1), 150);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [step, go]);

  const showing = Boolean(tour && step && onRoute);
  // Follow the bubble's size, and only when it really changes, so placing it can never feed back
  // into another render.
  useLayoutEffect(() => {
    const el = bubbleRef.current;
    if (!showing || !el) return undefined;
    const read = () => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      setSize((prev) => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
    };
    // Measured before paint, so a freshly shown bubble is placed by its real size and doesn't shift.
    read();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showing, entry, step]);

  // While a tour runs only the highlight and the bubble respond: panes take clicks, and scrolling is kept inside those two.
  const openTarget = rect && !hold ? step?.target : null;
  const openRef = useRef(null);
  openRef.current = openTarget;
  useEffect(() => {
    if (!showing) return undefined;
    const holder = (node) => {
      if (!node || node.nodeType !== 1) node = node?.parentElement;
      if (!node) return null;
      if (bubbleRef.current?.contains(node)) return bubbleRef.current;
      return openRef.current ? node.closest(`[data-tour="${openRef.current}"]`) : null;
    };
    const guard = (e, dx, dy) => {
      const box = holder(e.target);
      if (!box) return e.preventDefault();
      // A highlight taller than the screen can scroll the page under it, so all of it can be seen.
      const r = box.getBoundingClientRect();
      const clipped = box !== bubbleRef.current && (r.top < 0 || r.bottom > window.innerHeight);
      if (!clipped && !canScrollWithin(e.target, box, dx, dy)) e.preventDefault();
    };
    const onWheel = (e) => guard(e, e.deltaX, e.deltaY);
    let touch = null;
    const onTouchStart = (e) => {
      touch = e.touches[0] ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null;
    };
    const onTouchMove = (e) => {
      const t = e.touches[0];
      if (!t || !touch) return;
      guard(e, touch.x - t.clientX, touch.y - t.clientY);
      touch = { x: t.clientX, y: t.clientY };
    };
    const onKey = (e) => {
      const el = document.activeElement;
      const editable = el?.matches?.("input, textarea, select, [contenteditable='true']");
      if (!holder(el)) {
        if (SCROLL_KEYS.has(e.key) || e.key === " " || e.key === "Enter") e.preventDefault();
      } else if (SCROLL_KEYS.has(e.key) && !editable) {
        e.preventDefault();
      }
    };
    window.addEventListener("wheel", onWheel, { passive: false, capture: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true, capture: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false, capture: true });
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("wheel", onWheel, { capture: true });
      window.removeEventListener("touchstart", onTouchStart, { capture: true });
      window.removeEventListener("touchmove", onTouchMove, { capture: true });
      window.removeEventListener("keydown", onKey, true);
    };
  }, [showing]);

  useEffect(() => {
    if (!tour) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") writeActive(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tour]);

  // The bubble shows at once on every step; it sits centred until its target is found, then moves there.
  if (!showing) return null;
  const last = active.step === tour.steps.length - 1;
  const pos = placeBubble(rect, size);

  return createPortal(
    <>
      {rect ? (
        <Spot
          key={`spot-${entry}`}
          $fresh={entry > 0}
          $held={hold}
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
          }}
        />
      ) : (
        <Spot key="spot-none" $held={hold} style={{ top: "50%", left: "50%", width: 0, height: 0 }} />
      )}
      {blockPanes(openTarget ? rect : null).map((pane, i) => (
        <Block key={i} style={pane} aria-hidden data-tour-layer />
      ))}
      <Bubble
        key={`bubble-${entry}`}
        ref={bubbleRef}
        data-tour-layer
        $wide={step.wide}
        // visibility too: the pop animation overrides inline opacity, so a held bubble would flash up centred.
        style={hold ? { ...pos, opacity: 0, visibility: "hidden", pointerEvents: "none" } : pos}
        role="dialog"
        aria-label={tour.title}
      >
        <Close type="button" aria-label="End the walkthrough" onClick={() => writeActive(null)}>
          <FiX size={15} />
        </Close>
        <Eyebrow>
          {tour.title} · {active.step + 1} of {tour.steps.length}
          {step.isLocked && <LockPill>Growth plan and up</LockPill>}
        </Eyebrow>
        <Title>{step.title}</Title>
        <Body>{missing && step.target ? step.fallback || step.body : step.body}</Body>
        <Foot>
          <Dots aria-hidden>
            {tour.steps.map((s, i) => (
              <span key={i} className={i === active.step ? "on" : ""} />
            ))}
          </Dots>
          {active.step > 0 && !step.waiting && !step.noBack && (
            <Btn type="button" onClick={() => go(-1)}>
              Back
            </Btn>
          )}
          {step.fill && !missing && (
            <Btn type="button" $soft onClick={() => fillField(step.fill.target || step.target, step.fill.value)}>
              {step.fill.label || "Fill in an example"}
            </Btn>
          )}
          {step.waiting ? (
            <Waiting>{step.waiting}</Waiting>
          ) : (step.advanceOnClick || step.advanceWhen) && !missing && !(active.dir === -1 && already) ? (
            // An action step only moves on when the user really does it; skipping would strand the tour.
            <Hint>{step.hint || "Click the highlighted button to continue"}</Hint>
          ) : (
            <Btn
              type="button"
              $primary
              disabled={step.requireFilled && !missing && !filled}
              title={step.requireFilled && !missing && !filled ? "Fill this in first" : undefined}
              onClick={() => go(1)}
            >
              {last ? "Done" : "Next"}
            </Btn>
          )}
        </Foot>
      </Bubble>
    </>,
    document.body,
  );
}
